import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { ServiceType, ProviderId, TimeSlot } from '../../types';
import { TIMEZONES } from '../../constants';
import { fetchAvailability } from '../../utils/api';
import { detectUserTimezone, getTimezoneAbbreviation } from '../../utils/timezone';
import Select from '../Select';
import Card from '../Card';
import Loader from '../Loader';
import Modal from '../Modal';
import Button from '../Button';

interface Step3Props {
  serviceId: ServiceType;
  providerId: ProviderId;
  selectedSlot: TimeSlot | null;
  selectedTimezone: string;
  onSelectSlot: (slot: TimeSlot) => void;
  onTimezoneChange: (timezone: string) => void;
}

function groupSlotsByDate(slots: TimeSlot[]): Record<string, TimeSlot[]> {
  const grouped: Record<string, TimeSlot[]> = {};

  slots.forEach((slot) => {
    const dateKey = slot.start.split('T')[0];
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(slot);
  });

  Object.values(grouped).forEach((list) => {
    list.sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );
  });

  return grouped;
}

export default function Step3TimeSlot({
  serviceId,
  providerId,
  selectedSlot,
  selectedTimezone,
  onSelectSlot,
  onTimezoneChange
}: Step3Props) {
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [showTimezoneSelector, setShowTimezoneSelector] = useState(false);

  useEffect(() => {
    if (!selectedTimezone) {
      onTimezoneChange(detectUserTimezone());
    }
  }, [selectedTimezone, onTimezoneChange]);

  useEffect(() => {
    if (!selectedTimezone) return;

    setLoading(true);
    setError(null);

    fetchAvailability(serviceId, providerId, selectedTimezone)
      .then((response) => {
        const ordered = response.slots.slice().sort(
          (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
        );
        setSlots(ordered);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load available times');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [serviceId, providerId, selectedTimezone]);

  const groupedSlots = useMemo(() => groupSlotsByDate(slots), [slots]);
  const orderedDates = useMemo(() => {
    const keys = Object.keys(groupedSlots);
    keys.sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );
    return keys;
  }, [groupedSlots]);

  useEffect(() => {
    if (orderedDates.length === 0) {
      setSelectedDateKey(null);
      return;
    }

    setSelectedDateKey((current) =>
      current && orderedDates.includes(current) ? current : orderedDates[0]
    );
  }, [orderedDates]);

  const selectedDateSlots = selectedDateKey
    ? groupedSlots[selectedDateKey] ?? []
    : [];

  const referenceSlot = selectedSlot ?? selectedDateSlots[0] ?? slots[0];
  const referenceDate = referenceSlot ? parseISO(referenceSlot.start) : new Date();
  const selectedTimezoneAbbr = getTimezoneAbbreviation(selectedTimezone, referenceDate);

  const handleSlotSelection = (slot: TimeSlot) => {
    onSelectSlot(slot);
    setModalOpen(false);
  };

  const formatSlot = (slot: TimeSlot) =>
    formatInTimeZone(parseISO(slot.start), selectedTimezone, 'h:mm a');

  const formatSummary = (slot: TimeSlot) => {
    const start = parseISO(slot.start);
    const dateStr = format(start, 'EEEE, MMMM d');
    const timeStr = formatInTimeZone(start, selectedTimezone, 'h:mm a');
    const tzAbbr = getTimezoneAbbreviation(selectedTimezone, start);
    return `${dateStr} at ${timeStr} ${tzAbbr}`;
  };

  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xl)',
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto'
  };

  const headerStyles: CSSProperties = {
    textAlign: 'center',
    marginBottom: 'var(--spacing-md)'
  };

  const titleStyles: CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    marginBottom: 'var(--spacing-sm)'
  };

  const subtitleStyles: CSSProperties = {
    fontSize: '1.125rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6
  };

  const timezoneContainerStyles: CSSProperties = {
    maxWidth: '400px',
    margin: '0 auto',
    width: '100%'
  };

  const timezoneDisplayStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--spacing-sm)'
  };

  const timezoneTextStyles: CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--color-text-secondary)'
  };

  const timezoneButtonStyles: CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'var(--color-primary)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 600,
    textDecoration: 'underline',
    padding: 0
  };

  const timezoneSelectWrapperStyles: CSSProperties = {
    marginTop: 'var(--spacing-sm)'
  };

  const summaryCardStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
    alignItems: 'center',
    textAlign: 'center'
  };

  const summaryPrimaryTextStyles: CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--color-secondary)'
  };

  const summarySecondaryTextStyles: CSSProperties = {
    color: 'var(--color-text-secondary)',
    maxWidth: '320px'
  };

  const errorStyles: CSSProperties = {
    padding: 'var(--spacing-xl)',
    textAlign: 'center',
    color: 'var(--color-error)',
    fontSize: '1rem'
  };

  const noSlotsStyles: CSSProperties = {
    padding: 'var(--spacing-2xl)',
    textAlign: 'center',
    color: 'var(--color-text-secondary)',
    fontSize: '1rem'
  };

  const modalLayoutStyles: CSSProperties = {
    display: 'flex',
    gap: 'var(--spacing-xl)',
    flexWrap: 'wrap'
  };

  const dateColumnStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-sm)',
    minWidth: '220px',
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'visible',
    padding: '0 var(--spacing-sm)'
  };

  const timeColumnStyles: CSSProperties = {
    flex: 1,
    minWidth: '260px',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-sm)',
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'visible',
    padding: '0 var(--spacing-sm)'
  };

  const timezoneHintStyles: CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-sm)'
  };

  const dateCardStyles: CSSProperties = {
    padding: 'var(--spacing-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
    margin: '0 var(--spacing-sm)',
    width: 'calc(100% - 2 * var(--spacing-md))',
    transformOrigin: 'center'
  };

  const timeSlotCardStyles: CSSProperties = {
    padding: 'var(--spacing-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
    margin: '0 var(--spacing-sm)',
    transformOrigin: 'center',
    width: 'calc(100% - 2 * var(--spacing-md))'
  };

  const dateLabelStyles: CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em'
  };

  const dateValueStyles: CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--color-secondary)'
  };

  return (
    <div style={containerStyles}>
      <motion.div
        style={headerStyles}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 style={titleStyles}>Choose Your Time</h1>
        <p style={subtitleStyles}>
          Select a date and time that works best for you
        </p>
      </motion.div>

      <motion.div
        style={timezoneContainerStyles}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div style={timezoneDisplayStyles}>
          <span style={timezoneTextStyles}>
            Times shown in <strong>{selectedTimezone}</strong>
            {selectedTimezoneAbbr ? ` (${selectedTimezoneAbbr})` : ''}
          </span>
          <button
            type="button"
            style={timezoneButtonStyles}
            onClick={() => setShowTimezoneSelector((prev) => !prev)}
          >
            Change timezone
          </button>
        </div>
        {showTimezoneSelector && (
          <div style={timezoneSelectWrapperStyles}>
            <Select
              label="Select a Time Zone"
              value={selectedTimezone}
              onChange={(e) => {
                onTimezoneChange(e.target.value);
                setShowTimezoneSelector(false);
              }}
              options={TIMEZONES}
              required
            />
          </div>
        )}
      </motion.div>

      {loading && <Loader text="Loading available times..." />}

      {error && <div style={errorStyles}>{error}</div>}

      {!loading && !error && slots.length === 0 && (
        <div style={noSlotsStyles}>
          No available times found. Please try a different time zone or contact us directly.
        </div>
      )}

      {!loading && !error && slots.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card style={summaryCardStyles}>
            {selectedSlot ? (
              <>
                <div style={summarySecondaryTextStyles}>You&apos;re scheduled for</div>
                <div style={summaryPrimaryTextStyles}>{formatSummary(selectedSlot)}</div>
                <div style={summarySecondaryTextStyles}>
                  Need a different time? Browse the full calendar below.
                </div>
                <Button variant="outline" onClick={() => setModalOpen(true)}>
                  Change Time
                </Button>
              </>
            ) : (
              <>
                <div style={summaryPrimaryTextStyles}>Ready to find a time?</div>
                <div style={summarySecondaryTextStyles}>
                  Browse all available dates and times in your time zone.
                </div>
                <Button variant="primary" onClick={() => setModalOpen(true)}>
                  Browse Availability
                </Button>
              </>
            )}
          </Card>
        </motion.div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title="Select a Date & Time"
        maxWidth="900px"
      >
        <div style={timezoneHintStyles}>
          All times shown in {selectedTimezone}
          {selectedTimezoneAbbr ? <> ({selectedTimezoneAbbr})</> : null}
        </div>
        <div style={modalLayoutStyles}>
          <div style={dateColumnStyles}>
            {orderedDates.map((dateKey) => {
              const date = parseISO(`${dateKey}T00:00:00`);
              const isSelected = dateKey === selectedDateKey;
              return (
                <Card
                  key={dateKey}
                  onClick={() => setSelectedDateKey(dateKey)}
                  selected={isSelected}
                  style={dateCardStyles}
                >
                  <span style={dateLabelStyles}>{format(date, 'EEE')}</span>
                  <span style={dateValueStyles}>{format(date, 'MMM d')}</span>
                  <span style={timezoneHintStyles}>
                    {groupedSlots[dateKey]?.length || 0} {groupedSlots[dateKey]?.length === 1 ? 'slot' : 'slots'}
                  </span>
                </Card>
              );
            })}
            {orderedDates.length === 0 && (
              <div style={noSlotsStyles}>No available dates in the selected window.</div>
            )}
          </div>

          <div style={timeColumnStyles}>
            {selectedDateSlots.length === 0 ? (
              <div style={noSlotsStyles}>
                No times available for this date. Choose another day on the left.
              </div>
            ) : (
              selectedDateSlots.map((slot, index) => {
                const timeStr = formatSlot(slot);
                const startDate = parseISO(slot.start);
                const tzAbbr = getTimezoneAbbreviation(selectedTimezone, startDate);
                const isSelected =
                  selectedSlot?.start === slot.start && selectedSlot?.end === slot.end;

                return (
                  <Card
                    key={`${slot.start}-${index}`}
                    onClick={() => handleSlotSelection(slot)}
                    selected={isSelected}
                    style={timeSlotCardStyles}
                  >
                    <span style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-secondary)' }}>
                      {timeStr}
                    </span>
                    <span style={{ color: 'var(--color-text-secondary)' }}>{tzAbbr}</span>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
